import { useCallback, useEffect, useRef, useState } from 'react';
import API_ENDPOINTS from '../config/apiConfig';
import { tokenStorage, ACCESS_TOKEN_KEY } from '../services/apiService';

// ─────────────────────────────────────────────────────────────────────────────
// useJobStatusSocket
//
// One shared, receive-only WebSocket for the whole Jobs page. Opened when the
// page mounts and kept open for as long as the user is on the page — the
// connection itself IS the subscription, so there is nothing to send.
//
// Backend contract:
//   Connect : wss://<host>/comparisons/ws/jobs?token=<access_token>
//             (token in the query string because a browser WS handshake can't
//              carry an Authorization header). Server closes with 4401 if the
//              token is missing / invalid / expired.
//   Receive : one JSON message per status change —
//             {
//               type: "job_status_update",
//               job_id, job_name,
//               job_status: "RUNNING | READY | SCHEDULED",
//               run: { id, status, run_number, triggered_by, ... } | null
//             }
//             `run` is null only when a scheduled run fails config validation
//             before a run row can be created.
//   Send    : nothing. Anything sent is ignored server-side.
//
// The page merges each message by job_id (see onJobUpdate); events for jobs not
// currently on screen are simply ignored — filtering is intentionally the
// client's job.
// ─────────────────────────────────────────────────────────────────────────────

const MAX_BACKOFF = 15000;        // cap reconnect delay at 15s
const AUTH_CLOSE_CODE = 4401;     // server rejected the token

// Connection states surfaced to the UI.
export const SOCKET_STATE = {
    CONNECTING: 'connecting',
    LIVE: 'live',
    RECONNECTING: 'reconnecting',
    CLOSED: 'closed',
};

/**
 * @param {object}   opts
 * @param {function} opts.onJobUpdate   (message) => void  — a `job_status_update` payload
 * @param {function} opts.onReconnected () => void         — fired after a re-open, to resync
 *                                                            anything missed while disconnected
 * @param {boolean}  opts.enabled       gate the whole connection on/off
 */
export default function useJobStatusSocket({ onJobUpdate, onReconnected, enabled = true }) {
    const [socketState, setSocketState] = useState(SOCKET_STATE.CLOSED);
    // Consecutive failed connection attempts since the last successful open —
    // lets the UI escalate from a plain "reconnecting" notice to "give up and
    // refresh manually" once retries have failed a few times in a row.
    const [reconnectAttempts, setReconnectAttempts] = useState(0);

    const wsRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const reconnectTimerRef = useRef(null);
    const hasConnectedOnceRef = useRef(false);
    const manualCloseRef = useRef(false);
    // Breaks the connect <-> scheduleReconnect cycle without stale closures.
    const connectRef = useRef(() => { });

    // Keep callbacks in refs so the socket handlers never go stale and we don't
    // rebuild the connection on every render.
    const onJobUpdateRef = useRef(onJobUpdate);
    const onReconnectedRef = useRef(onReconnected);
    useEffect(() => { onJobUpdateRef.current = onJobUpdate; }, [onJobUpdate]);
    useEffect(() => { onReconnectedRef.current = onReconnected; }, [onReconnected]);

    const clearReconnectTimer = useCallback(() => {
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
    }, []);

    const scheduleReconnect = useCallback(() => {
        if (manualCloseRef.current || !enabled) return;
        setSocketState(SOCKET_STATE.RECONNECTING);
        const attempt = reconnectAttemptsRef.current++;
        setReconnectAttempts(reconnectAttemptsRef.current);
        const delay = Math.min(1000 * 2 ** attempt, MAX_BACKOFF);
        clearReconnectTimer();
        reconnectTimerRef.current = setTimeout(() => connectRef.current(), delay);
    }, [enabled, clearReconnectTimer]);

    const connect = useCallback(() => {
        if (!enabled) return;

        // Never stack sockets.
        const existing = wsRef.current;
        if (existing &&
            (existing.readyState === WebSocket.CONNECTING || existing.readyState === WebSocket.OPEN)) {
            return;
        }

        clearReconnectTimer();
        manualCloseRef.current = false;
        setSocketState(
            hasConnectedOnceRef.current ? SOCKET_STATE.RECONNECTING : SOCKET_STATE.CONNECTING
        );

        // Re-read the token on every (re)connect so a refresh performed elsewhere
        // in the app is picked up automatically.
        const token = tokenStorage.get(ACCESS_TOKEN_KEY);
        let ws;
        try {
            ws = new WebSocket(API_ENDPOINTS.JOBS.JOB_STATUS_WS(token));
        } catch (err) {
            console.error('Job status socket: construct failed', err);
            scheduleReconnect();
            return;
        }
        wsRef.current = ws;

        ws.onopen = () => {
            reconnectAttemptsRef.current = 0;
            setReconnectAttempts(0);
            setSocketState(SOCKET_STATE.LIVE);

            // A reconnect (not the first open) means status changes may have been
            // pushed while we were disconnected and won't be replayed — pull the
            // current truth so the page is correct.
            if (hasConnectedOnceRef.current) {
                onReconnectedRef.current?.();
            }
            hasConnectedOnceRef.current = true;
        };

        ws.onmessage = (event) => {
            let msg;
            try { msg = JSON.parse(event.data); } catch { return; }
            if (msg && msg.type === 'job_status_update') {
                onJobUpdateRef.current?.(msg);
            }
        };

        ws.onerror = () => {
            // onclose fires next and drives the reconnect.
        };

        ws.onclose = (event) => {
            // Ignore a stale socket's close if we've already moved on to a newer one.
            if (wsRef.current === ws) wsRef.current = null;

            if (manualCloseRef.current) {
                setSocketState(SOCKET_STATE.CLOSED);
                return;
            }

            // 4401 => token missing / invalid / expired. We still reconnect (the
            // token is re-read on the next attempt, catching a refresh elsewhere),
            // and the exponential backoff prevents a hot loop if it's truly expired.
            if (event.code === AUTH_CLOSE_CODE) {
                console.warn('Job status socket: auth rejected (4401) — will retry with a fresh token');
            }

            scheduleReconnect();
        };
    }, [enabled, scheduleReconnect, clearReconnectTimer]);

    // Keep connectRef pointed at the latest connect for the reconnect timer.
    useEffect(() => { connectRef.current = connect; }, [connect]);

    // Open on mount (when enabled); tear everything down on unmount / disable.
    useEffect(() => {
        if (!enabled) {
            manualCloseRef.current = true;
            clearReconnectTimer();
            try { wsRef.current?.close(); } catch (_) { /* noop */ }
            wsRef.current = null;
            setSocketState(SOCKET_STATE.CLOSED);
            return undefined;
        }

        manualCloseRef.current = false;
        connect();

        return () => {
            manualCloseRef.current = true;
            clearReconnectTimer();
            const ws = wsRef.current;
            wsRef.current = null;
            try { ws?.close(); } catch (_) { /* noop */ }
            setSocketState(SOCKET_STATE.CLOSED);
        };
    }, [enabled, connect, clearReconnectTimer]);

    // When the tab returns to the foreground, a backgrounded socket may have been
    // silently killed (mobile especially). If it isn't open, reconnect right away
    // instead of waiting out the backoff.
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState !== 'visible') return;
            if (!enabled || manualCloseRef.current) return;
            const ws = wsRef.current;
            if (!ws || ws.readyState === WebSocket.CLOSING || ws.readyState === WebSocket.CLOSED) {
                reconnectAttemptsRef.current = 0;   // treat as a fresh, immediate attempt
                setReconnectAttempts(0);
                clearReconnectTimer();
                connect();
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [enabled, connect, clearReconnectTimer]);

    // The browser's online/offline events fire immediately on local network
    // change, well before the socket itself would notice a silently dead
    // connection — use them to flip the UI to "reconnecting" right away and to
    // retry instantly (skipping backoff) as soon as the network is back.
    useEffect(() => {
        const onOffline = () => {
            if (!enabled || manualCloseRef.current) return;
            // Force-close the (likely still "open" as far as the browser knows)
            // socket so the normal onclose -> scheduleReconnect cycle actually
            // starts running and counting attempts, instead of just flipping the
            // displayed state while nothing retries underneath it.
            const ws = wsRef.current;
            if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
                try { ws.close(); } catch (_) { /* noop */ }
            } else {
                setSocketState(SOCKET_STATE.RECONNECTING);
            }
        };
        const onOnline = () => {
            if (!enabled || manualCloseRef.current) return;
            const ws = wsRef.current;
            if (!ws || ws.readyState === WebSocket.CLOSING || ws.readyState === WebSocket.CLOSED) {
                reconnectAttemptsRef.current = 0;
                setReconnectAttempts(0);
                clearReconnectTimer();
                connect();
            }
        };
        window.addEventListener('offline', onOffline);
        window.addEventListener('online', onOnline);
        return () => {
            window.removeEventListener('offline', onOffline);
            window.removeEventListener('online', onOnline);
        };
    }, [enabled, connect, clearReconnectTimer]);

    return {
        socketState,
        isLive: socketState === SOCKET_STATE.LIVE,
        reconnectAttempts,
    };
}